import React, {useEffect, useMemo, useState} from 'react';
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {useQueryClient} from "@tanstack/react-query";
import {CustomAxiosError} from "../../../../../logics/api_config";
import {MaterialReactTable, MRT_ActionMenuItem, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import {
    getMuiTableBodyCellProps,
    getMuiTableBodyRowProps,
    getMuiTableHeadCellProps,
    getMuiTablePaperProps
} from "../../../../ui/mui/mui_react_table/MuiReactTableStyles";
import {Delete, Edit} from "@mui/icons-material";
import {Avatar, Box, Grid, Popover} from "@mui/material";
import MuiReadOnlyBoxBody from "../../../../ui/mui/mui-display-data/MuiReadOnlyBoxBody";
import MRTMuiButton from "../../../../ui/mui/mui_react_table/MRTMuiButton";
import AddIcon from "@mui/icons-material/Add";
import Loading from "../../../../ui/Loading";
import MuiAlertDialog from "../../../../ui/mui/mui_modal/MuiAlertDialog";
import MuiFetchingSpinner from "../../../../ui/mui/MuiFetchingSpinner";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import Papa from "papaparse";
import {CSVLink} from "react-csv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MuiFabButtonBack from "../../../../ui/mui/mui_buttons/fab/extended/MuiFabButtonBack";
import {IMyCompanyWarehouse} from "../../../../../utils/interfaces/my_company/my_company_warehouse/IMyCompanyWarehouse";
import useApiDeleteMyCompanyWarehouse from "./api-hooks/useApiDeleteMyCompanyWarehouse";
import useApiGetMyCompanyWarehouses from "./api-hooks/useApiGetMyCompanyWarehouses";
import useCustomCellRenderer from "./useCustomCellRenderer";
import MyCompanyWarehouseForm from "./MyCompanyWarehouseForm";
import {FaWarehouse} from "react-icons/fa";


interface IObjCustomField extends IMyCompanyWarehouse {
    custom_field: JSX.Element;
}

interface Props {
    onSelect?: (obj: IMyCompanyWarehouse | undefined) => void,
    onClose?: () => void;
    alreadySelectedObj?: IMyCompanyWarehouse | undefined,
}


const MyCompanyWarehouseTable = ({onSelect, onClose, alreadySelectedObj}: Props) => {
    const theme = useTheme();
    const {t} = useTranslation()
    const queryClient = useQueryClient();

    const [selectedRow, setSelectedRow] = useState<IMyCompanyWarehouse | undefined>()
    const [rows, setRows] = useState<IMyCompanyWarehouse[] | undefined>()

    const [isEditFormOpen, setIsEditFormOpen] = useState<boolean>(false)

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const deleteMutation = useApiDeleteMyCompanyWarehouse(selectedRow?.id);
    const {mutateAsync: apiDelete} = deleteMutation;

    const handleConfirmDelete = async () => {
        try {
            if (onSelect && (alreadySelectedObj?.id === selectedRow?.id)) {
                onSelect(undefined);
            }
            await apiDelete({pk: selectedRow?.id});
        } catch (error) {
        }
        setDeleteConfirmationOpen(false);
    };
    const handleCancelDelete = () => {
        setDeleteConfirmationOpen(false);
    };


    const {
        data,
        isLoading,
        error,
        isFetching,
    } = useApiGetMyCompanyWarehouses();
    if (error) {
        const axiosError = error as CustomAxiosError;
        console.log(axiosError.response.data.detail);
    }


    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    const CustomCellRenderer: React.FC<{ row: IMyCompanyWarehouse, theme: any }> = ({row, theme}) => {
        const {renderIcons} = useCustomCellRenderer(row, theme);
        return renderIcons();
    };


    const [objsWithCustomField, setObjsWithCustomField] = useState<IObjCustomField[] | undefined>();
    useEffect(() => {
        const dataWithCustomField: IObjCustomField[] | undefined = rows?.map((row) => ({
            ...row,
            custom_field: <CustomCellRenderer row={row} theme={theme}/>
        }));
        setObjsWithCustomField(dataWithCustomField);
    }, [rows, theme]);


    // note_marco: export csv example
    const convertArrayOfObjectsToCSV = (data: IMyCompanyWarehouse[]): string => {
        // Create new array of objects without the unnecessary fields
        const reducedData = data.map(({...rest}) => rest);

        // Convert to CSV
        return Papa.unparse(reducedData, {
            delimiter: ';',
        });
    };


    const columns = useMemo<MRT_ColumnDef<IMyCompanyWarehouse>[]>(
        () => [
            {
                accessorKey: 'custom_field',
                header: '',
                enableColumnActions: false,
                enableColumnFilter: false,
                enableColumnOrdering: false,
                enableSorting: false,
            },
            {
                accessorKey: 'name',
                header: t('name'),
                filterFn: 'contains',

            },
            {
                accessorKey: 'address',
                header: t('address'),
                filterFn: 'contains',
            },
            {
                accessorKey: 'cap',
                header: t('cap'),
                filterFn: 'contains',
            },
            {
                accessorKey: 'city',
                header: t('city'),
                filterFn: 'contains',
                accessorFn: (row) => (row.city ? row.city : ''),

            },
            {
                accessorKey: 'nation',
                header: t('nation'),
                filterFn: 'contains',
                accessorFn: (row) => (row.nation ? row.nation.name : ''),

            },

        ],
        [rows]
    );


    const table = useMaterialReactTable<IMyCompanyWarehouse>({
        columns,
        defaultColumn: {
            maxSize: 400,
            minSize: 80,
            size: 80,
        },
        data: objsWithCustomField ? objsWithCustomField : [],
        columnFilterDisplayMode: 'popover',
        globalFilterFn: 'contains',
        enableFullScreenToggle: false,
        enableFilters: true,
        enableDensityToggle: true,
        muiTableHeadCellProps: getMuiTableHeadCellProps(theme.palette.primary.main),
        muiTableBodyCellProps: getMuiTableBodyCellProps(theme),
        muiTablePaperProps: getMuiTablePaperProps,
        enablePagination: objsWithCustomField && objsWithCustomField.length > 10,
        initialState: {
            density: 'compact',
            pagination: {pageSize: 25, pageIndex: 0},
        },
        mrtTheme: (theme) => ({
            baseBackgroundColor: theme.palette.backgroundAppGarageColor.main,

        }),
        muiTableBodyRowProps: getMuiTableBodyRowProps(theme),

        enableRowActions: true,
        renderRowActionMenuItems: ({closeMenu, row}) => [
            onSelect && onClose && (
                <MRT_ActionMenuItem
                    icon={<AdsClickIcon color="primary"/>}
                    key="select"
                    label={t("select")}
                    onClick={() => {
                        onSelect(row.original);
                        onClose();
                    }}
                    table={table}
                />),
            <MRT_ActionMenuItem
                icon={<Edit color="primary"/>}
                key="edit"
                label={t("edit")}
                onClick={(event) => {
                    setAnchorEl(event.currentTarget);
                    setSelectedRow(row.original);
                    setIsEditFormOpen(true)
                    closeMenu()
                }}
                table={table}
            />,
            <MRT_ActionMenuItem
                icon={<Delete color="warning"/>}
                key="delete"
                label={t("delete")}
                onClick={() => {
                    setSelectedRow(row.original);
                    setDeleteConfirmationOpen(true)
                    closeMenu()
                }}
                table={table}
            />,
        ],
        renderTopToolbarCustomActions: ({table}) => (
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <>
                    <Avatar sx={{
                        backgroundColor: 'white',
                        border: `2px solid ${theme.palette.primary.main}`,
                        marginRight: 1
                    }}>
                        <FaWarehouse color={theme.palette.primary.main}/>
                    </Avatar>
                    <MuiReadOnlyBoxBody label={''} value={'warehouses'}/>
                    <MRTMuiButton
                        icon={<AddIcon style={{color: theme.palette.primary.main, fontSize: '2rem'}}/>}
                        onClick={() => {
                            setSelectedRow(undefined);
                            setIsEditFormOpen(true)
                        }} label={'add warehouse'}/>
                    {rows &&
                        <CSVLink data={convertArrayOfObjectsToCSV(rows)} filename={'all_rows.csv'}>
                            {<FileDownloadIcon
                                style={{color: theme.palette.primary.main, fontSize: '2rem', marginTop: 8}}/>}
                        </CSVLink>
                    }

                </>
            </Box>
        ),
    });


    return <>
        {isLoading ? <Loading/> :
            <>
                {onClose &&
                    <Grid item xs={12} style={{textAlign: 'center'}}>
                        <Box className={'mt-2 gap-x-2 mb-2'}>
                            <MuiFabButtonBack onClick={onClose}/>
                        </Box>
                    </Grid>}

                <MaterialReactTable table={table}/>

                <MuiAlertDialog
                    open={isDeleteConfirmationOpen}
                    title="delete_confirmation"
                    content="ask_for_delete"
                    onClose={handleCancelDelete}
                    onConfirm={handleConfirmDelete}
                />

                <Popover
                    id={'edit_popover'}
                    open={isEditFormOpen}
                    anchorEl={anchorEl}
                    onClose={() => setIsEditFormOpen(false)}
                    anchorOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'center',
                        horizontal: 'center',
                    }}
                >
                    <MyCompanyWarehouseForm objToEdit={selectedRow ? selectedRow : null}
                                            onClose={() => setIsEditFormOpen(false)}/>
                </Popover>

                {isFetching &&
                    <MuiFetchingSpinner text="Fetching data..."/>
                }
            </>}
    </>;
};

export default MyCompanyWarehouseTable;
